/**
 * A generic class for pooling arbitrary resources.
 * - Resources are acquired lazily based on the function "acquirer".
 * - A maximum of "poolsize" resources can be acquired by the pool.
 * - A request to retrieve a resource times out after "timeout" (in milliseconds).
 * - Set timeout to 0 to disable timeouts.
 */

import { create } from '@thoughtspot/logger';

const logger = create('ResourcePool');

export enum QueueStrategy {
    FIFO = 'FIFO',
    LIFO = 'LIFO',
}

export class ResourcePool<T extends object> {
    // A (weak) reference to all objects currently lended out.
    private lended: WeakSet<T>;

    // Array of resources acquired but not currently lended out.
    public available: T[];

    // Separate queues for different strategies
    private fifoRequests: {
        resolver: (value?: T | PromiseLike<T>) => void;
        cancellationTimeout: any;
    }[];

    private lifoRequests: {
        resolver: (value?: T | PromiseLike<T>) => void;
        cancellationTimeout: any;
    }[];

    public acquiredCount: number;

    // Guard to prevent concurrent resource acquisition
    private isAcquiring = false;

    public constructor(
        public readonly timeout: number,
        public readonly poolsize: number,
        public readonly acquirer: () => T | PromiseLike<T>,
    ) {
        this.lended = new WeakSet();
        this.available = [];
        this.fifoRequests = [];
        this.lifoRequests = [];
        this.acquiredCount = 0;
    }

    public async retrieve(queueStrategy: QueueStrategy = QueueStrategy.FIFO): Promise<T> {
        const { timeout, available, lended } = this;

        if (available.length) {
            const res = available.pop()!;
            lended.add(res);
            return res;
        }

        return new Promise<T>((resolve, reject) => {
            const pendingRequest = {
                resolver: resolve,
                cancellationTimeout:
                    // eslint-disable-next-line @typescript-eslint/no-use-before-define
                    timeout && setTimeout(timeoutHandler, timeout),
            };

            // Add to appropriate queue based on strategy
            const targetQueue = queueStrategy === QueueStrategy.FIFO
                ? this.fifoRequests
                : this.lifoRequests;
            targetQueue.push(pendingRequest);

            this.onPotentialAvailability();

            function timeoutHandler() {
                const requestIdx = targetQueue.findIndex(
                    candidate => candidate === pendingRequest,
                );
                if (requestIdx === -1) {
                    return;
                }
                targetQueue.splice(requestIdx, 1);
                reject(new Error('Failed to acquire resource in due time'));
            }
        });
    }

    public returnResource(el: T): void {
        const { available, lended } = this;

        // This check helps find cases where the caller accidentally frees the same resource twice.
        if (!lended.has(el)) {
            logger.error('unknown resource returned');
            return;
        }

        lended.delete(el);
        available.push(el);

        this.onPotentialAvailability();
    }

    private async onPotentialAvailability(): Promise<void> {
        // Use while loop to avoid recursive call stack overflow and race conditions
        // Safeguard: track consecutive failures to prevent infinite loops
        // Scale max failures with pool size - larger pools can tolerate more failures
        const MAX_CONSECUTIVE_FAILURES = Math.max(3, this.poolsize);
        let consecutiveFailures = 0;

        while (true) {
            // STEP 1: Process ALL pending requests with available resources
            // Priority: FIFO queue first (initial viewport), then LIFO queue (recent scrolls)
            while (this.available.length && this.hasPendingRequests()) {
                const resource = this.available.pop()!;

                // Get next request: prioritize FIFO queue, then LIFO queue (most recent)
                let request;
                if (this.fifoRequests.length) {
                    // FIFO: take from front of queue (first-come-first-served)
                    request = this.fifoRequests.shift()!;
                } else {
                    // LIFO: take from end of queue (most recent request first)
                    request = this.lifoRequests.pop()!;
                }

                const { resolver, cancellationTimeout } = request;
                
                if (cancellationTimeout) {
                    clearTimeout(cancellationTimeout);
                }
                this.lended.add(resource);
                resolver(resource);
            }

            // STEP 2: Check if we need to acquire new resources
            // Guard against concurrent acquisitions - only ONE acquisition at a time
            if (
                !this.available.length &&
                this.hasPendingRequests() &&
                this.acquiredCount < this.poolsize &&
                !this.isAcquiring
            ) {
                this.isAcquiring = true;

                try {
                    this.acquiredCount += 1;
                    // eslint-disable-next-line no-await-in-loop
                    const resource = await this.acquirer();
                    this.available.push(resource);
                    // Reset failure counter on success
                    consecutiveFailures = 0;
                } catch (err) {
                    this.acquiredCount -= 1;
                    consecutiveFailures++;
                    logger.error('Failed to acquire resource', {
                        error: err,
                        consecutiveFailures,
                        fifoRequests: this.fifoRequests.length,
                        lifoRequests: this.lifoRequests.length,
                    });

                    // If we've failed too many times consecutively, stop trying
                    if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
                        logger.error('Max consecutive failures reached, stopping resource acquisition', {
                            consecutiveFailures,
                            fifoRequests: this.fifoRequests.length,
                            lifoRequests: this.lifoRequests.length,
                        });
                        this.isAcquiring = false;
                        break;
                    }
                } finally {
                    this.isAcquiring = false;
                }

                // After acquiring, loop back to process the new resource
                continue;
            }

            // No more work to do - exit the loop
            break;
        }
    }

    private hasPendingRequests(): boolean {
        return this.fifoRequests.length > 0 || this.lifoRequests.length > 0;
    }

    public evictResource(el: T): void {
        const { lended } = this;

        if (!lended.has(el)) {
            logger.error('unknown resource being evicted');
            return;
        }

        // SAFETY: Validate count before decrementing
        if (this.acquiredCount <= 0) {
            logger.warn('Cannot evict: acquired count already at zero');
            return;
        }

        lended.delete(el);
        this.acquiredCount -= 1;

        this.onPotentialAvailability();
    }
}
